import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Apple, Chrome, Share, Plus, Download as DownloadIcon, Smartphone, Monitor, HomeIcon, ChevronDown } from "lucide-react";
import AppShell from "../components/ui-r1/AppShell";
import { onInstallAvailable, promptInstall, isStandalone, isIosSafari } from "../pwa";

const APK_PUBLIC_PATH = "/downloads/bra-game.apk";

/**
 * /download — public route.  Two side-by-side cards (PWA install + Android APK)
 * plus a per-platform accordion of Add-to-Home-Screen instructions for iOS
 * Safari, Android Chrome and Desktop Chrome.
 */
export default function Download() {
  const [available, setAvailable] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [ios, setIos] = useState(false);
  const [apkAvailable, setApkAvailable] = useState(false);
  const [outcomeMsg, setOutcomeMsg] = useState("");
  const [open, setOpen] = useState("");

  useEffect(() => {
    setInstalled(isStandalone());
    setIos(isIosSafari());
    // Probe for the APK file — if the ops team hasn't dropped it in
    // /public/downloads yet, the card stays disabled with "Coming Soon".
    fetch(APK_PUBLIC_PATH, { method: "HEAD" }).then(
      (r) => setApkAvailable(r.ok),
      () => setApkAvailable(false),
    );
    return onInstallAvailable((p) => setAvailable(!!p));
  }, []);

  const install = async () => {
    setInstalling(true);
    const outcome = await promptInstall();
    setInstalling(false);
    setOutcomeMsg(outcome === "accepted" ? "Installed — check your home screen." : outcome === "dismissed" ? "Install cancelled." : "");
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="download-page">
        <div className="mb-8">
          <div className="text-[10px] tracking-[0.35em] uppercase text-pink font-semibold mb-2">PWA · Android · Desktop</div>
          <h1 className="font-display italic text-3xl sm:text-5xl leading-tight text-ink">
            Get the <span className="gold-text">Bra Game</span> app
          </h1>
          <p className="text-ink-2 mt-3 max-w-2xl text-sm sm:text-base">
            Install to your home screen for a full-screen, native-feeling experience — one tap, no store, no wait. Available on iOS, Android and desktop.
          </p>
        </div>

        {/* Cards */}
        <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
          {/* Card A — PWA install */}
          <section
            className="rounded-2xl border border-line/70 bg-elevated/40 p-5 sm:p-6 flex flex-col"
            data-testid="download-card-pwa"
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl overflow-hidden border border-line bg-base shrink-0">
                <img src="/icons/icon-192.png" alt="Bra Game" className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.3em] uppercase text-ink-3">Recommended</div>
                <div className="text-xl font-semibold text-ink leading-tight">Install as App</div>
                <div className="text-xs text-ink-3">All devices · No store · Instant</div>
              </div>
            </div>
            <ul className="text-sm text-ink-2 mt-4 space-y-1.5">
              <li className="flex items-start gap-2"><Chrome className="w-4 h-4 mt-0.5 shrink-0 text-pink" /> Standalone full-screen window — no browser chrome</li>
              <li className="flex items-start gap-2"><HomeIcon className="w-4 h-4 mt-0.5 shrink-0 text-pink" /> Home-screen icon like a native app</li>
              <li className="flex items-start gap-2"><Smartphone className="w-4 h-4 mt-0.5 shrink-0 text-pink" /> Works offline for the app shell</li>
            </ul>
            <div className="mt-5">
              {installed ? (
                <div className="h-11 rounded-lg border border-gold/60 text-gold text-sm font-semibold tracking-widest uppercase flex items-center justify-center" data-testid="download-installed-hint">
                  Already installed
                </div>
              ) : ios && !available ? (
                <button
                  disabled
                  className="h-11 w-full rounded-lg border border-line/70 text-ink-2 text-sm font-semibold tracking-widest uppercase flex items-center justify-center gap-2 cursor-default"
                  data-testid="download-ios-hint"
                >
                  <Share className="w-4 h-4" /> Use Safari · See iOS steps below
                </button>
              ) : (
                <button
                  onClick={install}
                  disabled={!available || installing}
                  className="h-11 w-full rounded-lg bg-brand-gradient text-white text-sm font-semibold tracking-widest uppercase shadow-glow-pink hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed transition"
                  data-testid="download-install-btn"
                >
                  {installing ? "Opening install prompt…" : available ? "Add to home screen" : "Available on Chrome / Edge / Android"}
                </button>
              )}
              {outcomeMsg && <div className="mt-2 text-[11px] text-ink-3 text-center" data-testid="download-install-status">{outcomeMsg}</div>}
            </div>
          </section>

          {/* Card B — Android APK */}
          <section
            className="rounded-2xl border border-line/70 bg-elevated/40 p-5 sm:p-6 flex flex-col"
            data-testid="download-card-apk"
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl overflow-hidden border border-line bg-base shrink-0 flex items-center justify-center">
                <Smartphone className="w-8 h-8 text-gold" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.3em] uppercase text-ink-3">Android only</div>
                <div className="text-xl font-semibold text-ink leading-tight">Native APK</div>
                <div className="text-xs text-ink-3">Direct install · Off-store · Auto-updating</div>
              </div>
            </div>
            <ul className="text-sm text-ink-2 mt-4 space-y-1.5">
              <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" /> Same experience with system-level polish</li>
              <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" /> Enable “Install from unknown sources” once</li>
              <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" /> Signed with our developer certificate</li>
            </ul>
            <div className="mt-5">
              {apkAvailable ? (
                <a
                  href={APK_PUBLIC_PATH}
                  download
                  className="h-11 w-full rounded-lg border border-gold/60 text-gold text-sm font-semibold tracking-widest uppercase flex items-center justify-center gap-2 hover:bg-gold/10 transition"
                  data-testid="download-apk-btn"
                >
                  <DownloadIcon className="w-4 h-4" /> Download APK
                </a>
              ) : (
                <button
                  disabled
                  className="h-11 w-full rounded-lg border border-line/70 text-ink-3 text-sm font-semibold tracking-widest uppercase flex items-center justify-center cursor-not-allowed"
                  data-testid="download-apk-soon"
                >
                  Coming soon
                </button>
              )}
            </div>
          </section>
        </div>

        {/* Instructions accordion */}
        <div className="mt-10">
          <h2 className="font-display italic text-2xl text-ink mb-4">Step-by-step</h2>
          <div className="space-y-3">
            <AccordionItem
              id="ios"
              icon={<Apple className="w-4 h-4" />}
              title="iOS · Safari"
              open={open === "ios"}
              onToggle={() => setOpen(open === "ios" ? "" : "ios")}
            >
              <ol className="list-decimal list-inside space-y-1.5 text-sm text-ink-2">
                <li>Open this site in <b>Safari</b> (not Chrome for iOS).</li>
                <li>Tap the <b>Share</b> icon <Share className="inline w-3.5 h-3.5 align-text-bottom" /> in the toolbar.</li>
                <li>Scroll and tap <b>Add to Home Screen</b> <Plus className="inline w-3.5 h-3.5 align-text-bottom" />.</li>
                <li>Confirm the name (defaults to “Bra Game”) and tap <b>Add</b>.</li>
              </ol>
            </AccordionItem>
            <AccordionItem
              id="android"
              icon={<Smartphone className="w-4 h-4" />}
              title="Android · Chrome"
              open={open === "android"}
              onToggle={() => setOpen(open === "android" ? "" : "android")}
            >
              <ol className="list-decimal list-inside space-y-1.5 text-sm text-ink-2">
                <li>Tap the <b>Install app</b> banner if it appears — otherwise…</li>
                <li>Open the <b>⋮</b> menu top-right → <b>Install app</b> or <b>Add to Home screen</b>.</li>
                <li>Confirm. The app icon appears in your launcher.</li>
              </ol>
            </AccordionItem>
            <AccordionItem
              id="desktop"
              icon={<Monitor className="w-4 h-4" />}
              title="Desktop · Chrome / Edge"
              open={open === "desktop"}
              onToggle={() => setOpen(open === "desktop" ? "" : "desktop")}
            >
              <ol className="list-decimal list-inside space-y-1.5 text-sm text-ink-2">
                <li>Look for the <b>Install</b> icon <DownloadIcon className="inline w-3.5 h-3.5 align-text-bottom" /> at the right of the address bar.</li>
                <li>Or open the browser menu → <b>Install Bra Game</b>.</li>
                <li>The app opens in its own window and pins to your dock / taskbar.</li>
              </ol>
            </AccordionItem>
          </div>
        </div>

        <div className="mt-8 text-xs text-ink-3">
          Trouble installing? <Link to="/dashboard" className="underline hover:text-ink">Contact support</Link> from your dashboard.
        </div>
      </div>
    </AppShell>
  );
}

function AccordionItem({ id, icon, title, open, onToggle, children }) {
  return (
    <div className="rounded-xl border border-line/70 bg-elevated/30 overflow-hidden" data-testid={`download-accordion-${id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-elevated/60 transition"
      >
        <span className="inline-flex items-center gap-2 text-ink font-semibold text-sm">
          {icon}
          {title}
        </span>
        <ChevronDown className={"w-4 h-4 text-ink-2 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-line/40">{children}</div>}
    </div>
  );
}
